// ========= START: App.js (COMPLETE REPLACEMENT) =========

import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import PronunciationChallenge from './components/PronunciationChallenge';
import UsernameSetupScreen from './components/UsernameSetupScreen';
import WelcomeScreen from './components/WelcomeScreen';
import { useSpeechRecognition } from './hooks/useSpeechRecognition';
import audioManager from './utils/audioManager';

// ========= START: App Component Definition =========

function App() {

  // ========= START: State Management Section =========
  const [appPhase, setAppPhase] = useState('LOADING');
  const [playerData, setPlayerData] = useState(null);
  const speechRecognition = useSpeechRecognition();
  // ========= END: State Management Section =========


  // ========= START: Core Navigation and Data Handling Functions (MODIFIED) =========

  const navigate = useCallback((newPhase) => {
    // MODIFICATION: The hash is now managed correctly for the WELCOME screen.
    const newHash = newPhase === 'WELCOME' ? '' : `#${newPhase}`;
    const newPath = window.location.pathname + newHash;

    // Only update history if the path is actually different.
    if (window.location.pathname + window.location.hash !== newPath) {
        // For game screens, we push to history to allow the back button to work.
        // For the welcome screen, we use replaceState to clear the hash without creating a back-button entry.
        if (newPhase === 'WELCOME') {
            window.history.replaceState({ phase: newPhase }, `BP Shout - Welcome`, window.location.pathname);
        } else {
            window.history.pushState({ phase: newPhase }, `BP Shout - ${newPhase}`, newHash);
        }
    }
    setAppPhase(newPhase);
  }, []);

  const navigateGame = useCallback((screen) => {
    const newPhase = `PRONUNCIATION_CHALLENGE_${screen}`;
    navigate(newPhase);
  }, [navigate]);

  const savePlayerData = (newPlayerData) => {
    try {
      localStorage.setItem('bpEchoPlayerData', JSON.stringify(newPlayerData));
    } catch (error) {
      console.error("Failed to save player data", error);
    }
  };

  const handleSaveUsername = (username) => {
    const newPlayerData = {
      username: username,
      level: 1,
      xp: 0,
      unlockPoints: 1,
      unlockedItems: {},
      equipped: {},
      wordMastery: {},
    };
    setPlayerData(newPlayerData);
    savePlayerData(newPlayerData);
    navigate('PRONUNCIATION_CHALLENGE_MENU');
  };

  const handleUpdatePlayerData = (updatedData) => {
    setPlayerData(updatedData);
    savePlayerData(updatedData);
  };

  const handleAddXp = useCallback((amount) => {
    setPlayerData(prevData => {
      if (!prevData) return null;
      const xpToNextLevel = 1000 + (prevData.level * 500);
      let newXp = prevData.xp + amount;
      let newLevel = prevData.level;
      let newUnlockPoints = prevData.unlockPoints || 0;
      while (newXp >= xpToNextLevel) {
        newLevel += 1;
        newXp -= xpToNextLevel;
        newUnlockPoints += 1;
        audioManager.playSFX('sfxLevelUp');
      }
      const newPlayerData = { ...prevData, xp: newXp, level: newLevel, unlockPoints: newUnlockPoints };
      savePlayerData(newPlayerData);
      return newPlayerData;
    });
  }, []);

  // ========= END: Core Navigation and Data Handling Functions (MODIFIED) =========


  // ========= START: Effect Hooks Section (RESTRUCTURED AND CORRECTED) =========

  // EFFECT 1: Handles INITIAL APP LOAD logic.
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('bpEchoPlayerData');
      if (savedData) {
        setPlayerData(JSON.parse(savedData));
      }
      
      const hash = window.location.hash.substring(1);
      const validGamePhases = [
        'PRONUNCIATION_CHALLENGE_MENU', 'PRONUNCIATION_CHALLENGE_SETUP', 'PRONUNCIATION_CHALLENGE_PLAY', 
        'PRONUNCIATION_CHALLENGE_STATS', 'PRONUNCIATION_CHALLENGE_CHARACTER', 'PRONUNCIATION_CHALLENGE_SETTINGS'
      ];

      if (hash && validGamePhases.includes(hash)) {
        setAppPhase(hash);
      } else {
        setAppPhase('WELCOME');
      }
    } catch (error) {
      console.error("Failed to load player data, routing to welcome screen.", error);
      localStorage.removeItem('bpEchoPlayerData');
      setAppPhase('WELCOME');
    }
  }, []);

  // EFFECT 2: Handles BROWSER HISTORY (Back/Forward buttons).
  useEffect(() => {
    const handlePopState = (event) => {
      // If the history state has a phase, use it. Otherwise, default to WELCOME.
      const newPhase = event.state?.phase || 'WELCOME';
      setAppPhase(newPhase);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // EFFECT 3: Handles AUDIO UNLOCK globally.
  useEffect(() => {
    const unlockAudioOnFirstInteraction = () => {
      audioManager.unlockAudio();
      window.removeEventListener('click', unlockAudioOnFirstInteraction);
      window.removeEventListener('touchstart', unlockAudioOnFirstInteraction);
    };
    window.addEventListener('click', unlockAudioOnFirstInteraction);
    window.addEventListener('touchstart', unlockAudioOnFirstInteraction);
    return () => {
      window.removeEventListener('click', unlockAudioOnFirstInteraction);
      window.removeEventListener('touchstart', unlockAudioOnFirstInteraction);
    };
  }, []);

  // EFFECT 4: Manages MICROPHONE state.
  useEffect(() => {
    if (appPhase !== 'PRONUNCIATION_CHALLENGE_PLAY') {
      speechRecognition.stopListening();
    }
  }, [appPhase, speechRecognition]);

  // ========= END: Effect Hooks Section (RESTRUCTURED AND CORRECTED) =========


  // ========= START: Render Logic Section =========

  const renderContent = () => {
    if (appPhase === 'LOADING') {
      return <div>Loading Game...</div>;
    }

    switch (appPhase) {
      case 'WELCOME':
        return <WelcomeScreen onStart={() => {
          if (playerData) {
            navigate('PRONUNCIATION_CHALLENGE_MENU');
          } else {
            navigate('USERNAME_SETUP');
          }
        }} />;

      case 'USERNAME_SETUP':
        return <UsernameSetupScreen onSave={handleSaveUsername} />;

      default:
        if (appPhase.startsWith('PRONUNCIATION_CHALLENGE_')) {
          if (!playerData) {
            navigate('WELCOME');
            return <div>Loading Game...</div>; 
          }
          return (
            <PronunciationChallenge
              phase={appPhase.substring('PRONUNCIATION_CHALLENGE_'.length)}
              onNavigate={navigateGame}
              onExit={() => navigate('WELCOME')} // MODIFICATION: This now correctly calls navigate.
              onNavigateToApp={() => navigate('PRONUNCIATION_CHALLENGE_MENU')}
              speechRecognition={speechRecognition}
              playerData={playerData}
              onUpdatePlayerData={handleUpdatePlayerData}
              onAddXp={handleAddXp}
            />
          );
        }
        return <div>An unknown error occurred. Please refresh the page.</div>;
    }
  };

  // ========= END: Render Logic Section =========


  // ========= START: Main Component Return =========

  return (
    <div className="App" data-phase={appPhase}>
      <main className="main-content-area">
        {!speechRecognition.hasRecognitionSupport ? (
          <p>Speech Recognition is not supported. Please use a modern browser like Chrome or Safari.</p>
        ) : (
          renderContent()
        )}
      </main>
    </div>
  );
  // ========= END: Main Component Return =========
}

export default App;

// ========= END: App.js (COMPLETE REPLACEMENT) =========